import { type JSX } from "react";
import { useForm } from "react-hook-form";
import { ChangeUserPasswordRequestBody } from "common";
import FormInputComponent from "@via/components/inputs/form-input-component.tsx";
import { requiredValidation } from "@via/utils/form-validators.tsx";
import { Spinner } from "@via/components/loading/loading.tsx";
import { useChangePassword } from "@via/api/auth.api.tsx";
import { useImpersonationUser } from "@via/hooks/useImpersonationUser.tsx";
import PasswordInput from "@via/components/password-input/password-input.tsx";

type ChangeUserPasswordType = ChangeUserPasswordRequestBody & {
  confirmPassword: string;
};

export default function ChangePassword(): JSX.Element {
  const { mutate: changeUserPassword, isPending: isChangeUserPasswordLoading } =
    useChangePassword();

  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<ChangeUserPasswordType>({
    mode: "onChange",
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const newPasswordWatch = watch("newPassword");
  const impersonationId = useImpersonationUser();
  const handleChangePassword = async (formData: ChangeUserPasswordType) => {
    const { oldPassword, newPassword } = formData;
    await changeUserPassword({
      oldPassword,
      newPassword,
      ownerId: impersonationId,
    });
  };

  const onSubmit = handleSubmit(handleChangePassword);

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Password Change</h2>
      </div>
      <form
        onSubmit={e => {
          e.preventDefault();
          onSubmit(e);
        }}>
        <div className="my-5">
          <div className="w-80">
            <FormInputComponent
              control={control}
              type="password"
              name="oldPassword"
              label="Old Password"
              validations={requiredValidation("Old Password")}
              className={"py-2"}
            />
          </div>
          <PasswordInput
            register={register}
            registerValue={"newPassword"}
            watch={watch}
            errors={errors}
            mainClass={"w-80 mt-4"}
          />
          <div className="w-80 mt-4">
            <FormInputComponent
              control={control}
              type="password"
              name="confirmPassword"
              label="Confirm New Password"
              validations={{
                ...requiredValidation("Confirm New Password"),
                validate: value =>
                  value === newPasswordWatch || "Passwords must match",
              }}
              className={"py-2"}
            />
          </div>
        </div>
        <button
          id="change-password"
          type="submit"
          disabled={isChangeUserPasswordLoading}
          className="inline-flex justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600">
          <Spinner loading={isChangeUserPasswordLoading}>Save</Spinner>
        </button>
      </form>
    </div>
  );
}
